import React from "react";
import { useProductMode } from "@/lib/productMode";
import { Link, useLocation } from "react-router-dom";
import { Mail, ChevronDown } from "lucide-react";

// Phase 10 — persistent site footer, rendered on every page via App.js.
// Judgement calls (per spec): NOT rendered on /admin (console context, the
// long tool page reads wrong with marketing links beneath it) or /certificate
// (print view). The suspended-account view bypasses ShellRoutes entirely so
// it never sees the footer.
const COLUMNS = [
  {
    title: "Discover",
    links: [
      { to: "/explore", label: "Ways to take part", testId: "footer-explore-link" },
      // Was "Ways to help", which sat directly under "Ways to take part" and
      // read as the same link twice — and neither matched the page, whose own
      // h1 and <title> are "Where this goes". Named after the destination now.
      { to: "/pathways", label: "Where this goes", testId: "footer-pathways-link" },
      { to: "/collectives", label: "Collectives", testId: "footer-collectives-link" },
      { to: "/stories", label: "Stories", testId: "footer-stories-link" },
      { to: "/recognition", label: "Recognition", testId: "footer-recognition-link" },
    ],
  },
  {
    title: "Get involved",
    links: [
      { to: "/help-request/new", label: "Ask a neighbour", testId: "footer-ask-link" },
      { to: "/post-opportunity", label: "Host an event", testId: "footer-host-link" },
      { to: "/partners", label: "For organisations", testId: "footer-partners-link" },
      { to: "/partners#councils", label: "For councils", testId: "footer-councils-link" },
      { to: "/for-professionals", label: "For professionals", testId: "footer-professionals-link" },
      { to: "/donate", label: "Donate", testId: "footer-donate-link" },
    ],
  },
  {
    title: "About",
    links: [
      { to: "/", label: "Home", testId: "footer-home-link" },
      { to: "/about", label: "About", testId: "footer-about-link" },
      { to: "/guide", label: "How it works", testId: "footer-guide-link" },
      { to: "/community", label: "Blog & News", testId: "footer-blog-news-link" },
      { to: "/safeguarding", label: "Safeguarding", testId: "footer-safeguarding-link" },
      { to: "/safety", label: "Safety Guide", testId: "footer-safety-link" },
      { to: "/privacy", label: "Privacy", testId: "footer-privacy-link" },
    ],
  },
];

export default function Footer() {
  const pm = useProductMode();
  const { pathname } = useLocation();
  const hidden = pathname === "/admin" || pathname.startsWith("/admin/") ||
    pathname === "/certificate" || pathname.startsWith("/certificate/") ||
    /^\/collectives\/[^/]+\/poster$/.test(pathname) ||   // print view (17-C)
    /^\/impact\/[^/]+\/pack$/.test(pathname) ||          // print view (17-D)
    pathname.startsWith("/evidence/");                   // print view (17-D)
  if (hidden) return null;

  return (
    <footer data-testid="site-footer" className="mt-16 border-t border-forest/10 bg-cream/60">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Desktop only. On a phone this block sat above three collapsed rows
              as a wordmark plus a tagline the copyright line repeats verbatim
              two inches below it — the brand said twice in one footer. Nothing
              is lost: "© CivicHearth. A street that looks after itself." is
              still there. */}
          <div className="hidden sm:block">
            <Link to="/" data-testid="footer-logo" className="inline-flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-forest flex items-center justify-center text-white font-display font-semibold text-sm">C</div>
              <span className="font-display text-base tracking-tight text-forest">Civic<span className="text-terracotta">.</span>Hearth</span>
            </Link>
            <p className="text-xs text-neutral-500 mt-3 max-w-[220px] leading-relaxed">
              A street that looks after itself.
            </p>
          </div>
          {/* Collapsed on mobile (2026-08). Stacked, the four link columns were
              825px of a 1,003px footer — over a screen of links under every
              page on a phone, to reach twenty destinations almost nobody is
              looking for at the bottom of a scroll. <details> keeps every link
              in the DOM (so they're still crawlable and still findable by
              in-page search) while costing four rows instead of four columns.
              `open` above sm returns the normal grid on wider screens, where
              the height is free. */}
          {COLUMNS.map((col) => ({ ...col, links: pm.links(col.links) })).filter((col) => col.links.length).map((col) => (
            <details
              key={col.title}
              open={typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches}
              className="group border-b border-forest/10 sm:border-0 sm:open"
              data-testid={`footer-col-${col.title.toLowerCase()}`}
            >
              <summary className="flex items-center justify-between py-3 sm:py-0 cursor-pointer list-none sm:pointer-events-none [&::-webkit-details-marker]:hidden">
                <span className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">{col.title}</span>
                <ChevronDown className="w-4 h-4 text-forest/40 transition-transform group-open:rotate-180 sm:hidden" aria-hidden="true" />
              </summary>
              <ul className="mt-1 pb-3 space-y-2 sm:mt-3 sm:pb-0">
                {col.links.map((l) => (
                  <li key={l.to + l.label}>
                    <Link to={l.to} data-testid={l.testId} className="text-sm text-neutral-600 hover:text-forest transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
                {col.title === "About" && (
                  <li>
                    <a href="mailto:hello@civichearth.com" data-testid="footer-contact-link" className="text-sm text-neutral-600 hover:text-forest transition-colors inline-flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" /> hello@civichearth.com
                    </a>
                  </li>
                )}
              </ul>
            </details>
          ))}
        </div>
        <div className="mt-8 pt-6 border-t border-forest/10 flex flex-col sm:flex-row gap-2 justify-between text-xs text-neutral-500">
          <div>© {new Date().getFullYear()} CivicHearth. A street that looks after itself.</div>
          <div>Made with care for civic society.</div>
        </div>
      </div>
    </footer>
  );
}
